import React from 'react';
import { Container, Paper, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { AnimalCapacity, FarmDocuments, CarbonFootprint, FeedConversion } from '../../components';
import { useTranslation } from 'react-i18next';
import Divider from '@mui/material/Divider';
import { Permission } from '../../constants/permissions';
import { usePermission } from '../../hooks/usePermission';

interface FarmInfoTab {
  option: string;
  id: string;
  labelKey: string;
  permission: Permission;
  render: () => React.ReactElement;
}

const TABS: FarmInfoTab[] = [
  {
    option: 'capacity',
    id: 'animalCapacityTab',
    labelKey: 'f-farm-anim-cap',
    permission: Permission.VIEW_FARM_INFO,
    render: () => <AnimalCapacity />
  },
  {
    option: 'documents',
    id: 'docsTab',
    labelKey: 'f-documents',
    permission: Permission.VIEW_FARM_DOCUMENTS,
    render: () => <FarmDocuments />
  },
  {
    option: 'carbon-footprint',
    id: 'carbonFootprintTab',
    labelKey: 'f-carbon-footprint',
    permission: Permission.VIEW_FARM_INFO,
    render: () => <CarbonFootprint />
  },
  {
    option: 'feed-conversion',
    id: 'feedConversionTab',
    labelKey: 'f-feed-conversion',
    permission: Permission.VIEW_FARM_INFO,
    render: () => <FeedConversion />
  }
];

const FarmInfo: React.FC<any> = () => {
  const styles = sxStyles(useTheme());
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermission();

  // /farm/documents has its own route, so it arrives without the :option parameter the other three carry.
  const option = location.pathname.split('/').filter(Boolean)[1];

  // A plain user reaches the documents tab through an export notification and nothing else here, so the tabs
  // they cannot open are not shown - clicking one would only bounce them off the page.
  const tabs = React.useMemo(() => TABS.filter((it) => hasPermission(it.permission)), [hasPermission]);

  const selectedIndex = Math.max(
    tabs.findIndex((it) => it.option === option),
    0
  );

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    navigate(`/farm/${tabs[newValue].option}`);
  };

  const selected = tabs[selectedIndex];

  return (
    <Container maxWidth={false} sx={{ maxWidth: 1280 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container>
            <Grid item container md={4} xs={12} spacing={1}>
              <Grid item>
                <HomeWorkIcon fontSize="large" color="primary" />
              </Grid>
              <Grid item>
                <Typography component="h2" variant="h6" color="primary">
                  {t('f-farm-info')}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid container direction="column" sx={styles.mt1}>
            <Tabs value={selectedIndex} onChange={handleTabChange}>
              {tabs.map((it) => (
                <Tab key={it.option} label={t(it.labelKey)} id={it.id} />
              ))}
            </Tabs>
            <Divider />
          </Grid>

          <Paper sx={styles.paper} elevation={1}>
            <Grid container>
              <Grid item xs={12} md={12}>
                {selected && selected.render()}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default FarmInfo;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column',
      minHeight: 200
    },
    spinnerBox: {
      display: 'flex',
      justifyContent: 'center'
    },
    formControl: {
      margin: theme.spacing(1)
    },
    mt1: {
      marginTop: theme.spacing(1)
    }
  }) as const;
